import {
  MedplumClient,
  getReferenceString,
  createReference,
} from '@medplum/core';
import {
  Attachment,
  CodeableConcept,
  DiagnosticReport,
  Patient,
} from '@medplum/fhirtypes';
import {
  ARKON,
  EXTERNAL_CS,
  PRENATAL_CHECK_REASON_EXTENSION_URL,
  PRENATAL_CHECK_TEST_TYPE_EXTENSION_URL,
} from '@/consts/systems.consts';
import {
  PrenatalCheckFormState,
  PrenatalCheckSaveParams,
  PrenatalCheckResultValue,
  MEDICAL_HISTORY_REPORT_CATEGORIES,
  PRENATAL_CHECK_CODES_WITH_REASON,
  PRENATAL_CHECK_CODES_WITH_TEST_TYPE,
} from './medical-history.types';
import {
  buildConclusionCode,
  getReportResultValueFromCode,
} from '../../files-tab/utils/report-files.utils';
import { ReportResultValue } from '../../files-tab/utils/report-files.consts';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const PDF_MIME_TYPE = 'application/pdf';

export const PRENATAL_CHECK_TEST_TYPE_OPTIONS = [
  { value: 'Απλός καρυότυπος', label: 'Απλός καρυότυπος' },
  { value: 'Μοριακός καρυότυπος', label: 'Μοριακός καρυότυπος' },
  { value: 'WES', label: 'WES' },
  { value: 'Στοχευμένος έλεγχος', label: 'Στοχευμένος έλεγχος' },
] as const;

export const PRENATAL_CHECK_RESULT_OPTIONS = [
  {
    value: 'Φυσιολογικό' as PrenatalCheckResultValue,
    label: 'Φυσιολογικό',
    snomedCode: EXTERNAL_CS.SNOMED.CODES.NORMAL_RESULT,
    reportResultValue: 'NORMAL' as ReportResultValue,
  },
  {
    value: 'Παθολογικό' as PrenatalCheckResultValue,
    label: 'Παθολογικό',
    snomedCode: EXTERNAL_CS.SNOMED.CODES.ABNORMAL_RESULT,
    reportResultValue: 'ABNORMAL' as ReportResultValue,
  },
] as const;

// Helper to map ReportResultValue to PrenatalCheckResultValue for UI
export const mapReportResultToPrenatalResult = (
  reportResult: ReportResultValue | null
): PrenatalCheckResultValue | null => {
  if (!reportResult) return null;
  const option = PRENATAL_CHECK_RESULT_OPTIONS.find(
    (opt) => opt.reportResultValue === reportResult
  );
  return option?.value ?? null;
};

// Helper to map PrenatalCheckResultValue to ReportResultValue
export const mapPrenatalResultToReportResult = (
  prenatalResult: PrenatalCheckResultValue | null
): ReportResultValue | null => {
  if (!prenatalResult) return null;
  const option = PRENATAL_CHECK_RESULT_OPTIONS.find(
    (opt) => opt.value === prenatalResult
  );
  return option?.reportResultValue ?? null;
};

// -----------------------------------------------------------------------------
// Initial Form State
// -----------------------------------------------------------------------------

export const createInitialPrenatalCheckFormState =
  (): PrenatalCheckFormState => ({
    checkType: null,
    reasonForTest: '',
    testType: null,
    result: null,
    observations: '',
    date: new Date().toISOString().split('T')[0],
    file: null,
  });

// -----------------------------------------------------------------------------
// Map DiagnosticReport to Form State
// -----------------------------------------------------------------------------

export const mapReportToFormState = (
  report: DiagnosticReport
): PrenatalCheckFormState => {
  const reasonExt = report.extension?.find(
    (ext) => ext.url === PRENATAL_CHECK_REASON_EXTENSION_URL
  );
  const testTypeExt = report.extension?.find(
    (ext) => ext.url === PRENATAL_CHECK_TEST_TYPE_EXTENSION_URL
  );

  // const resultCoding = report.conclusionCode?.[0]?.coding?.[0];
  // const result = PRENATAL_CHECK_RESULT_OPTIONS.find(
  //   (opt) => opt.snomedCode === resultCoding?.code
  // )?.value ?? null;

  const resultValue = getReportResultValueFromCode(
    report.conclusionCode?.[0]?.coding?.[0]?.code
  );

  return {
    checkType: report.code || null,
    reasonForTest: reasonExt?.valueString || '',
    testType:
      (testTypeExt?.valueString as PrenatalCheckFormState['testType']) || null,
    result: (resultValue as ReportResultValue | undefined) || null,
    observations: report.conclusion || '',
    date: report.effectiveDateTime?.slice(0, 10) || '',
    file: null, // Files are not loaded back into theform
  };
};

// -----------------------------------------------------------------------------
// Conditional Field Helpers
// -----------------------------------------------------------------------------

export const shouldShowReasonField = (
  checkType: CodeableConcept | null
): boolean => {
  if (!checkType?.coding?.[0]?.code) return false;
  const code = checkType.coding[0].code;
  return PRENATAL_CHECK_CODES_WITH_REASON.includes(
    code as (typeof PRENATAL_CHECK_CODES_WITH_REASON)[number]
  );
};

export const shouldShowTestTypeField = (
  checkType: CodeableConcept | null
): boolean => {
  if (!checkType?.coding?.[0]?.code) return false;
  const code = checkType.coding[0].code;
  return PRENATAL_CHECK_CODES_WITH_TEST_TYPE.includes(
    code as (typeof PRENATAL_CHECK_CODES_WITH_TEST_TYPE)[number]
  );
};

// -----------------------------------------------------------------------------
// Validation
// -----------------------------------------------------------------------------

export const isValidDate = (date: string): boolean => {
  if (!date || date.trim() === '') return false;
  const dateObj = new Date(date);
  const now = new Date();
  return !isNaN(dateObj.getTime()) && dateObj <= now;
};

export const validateFileForUpload = (file: File | null) => {
  if (!file) {
    return { isValid: true, isPdf: true, isSizeValid: true };
  }
  const isPdf = file.type === PDF_MIME_TYPE;
  const isSizeValid = file.size <= MAX_FILE_SIZE;
  return {
    isValid: isPdf && isSizeValid,
    isPdf,
    isSizeValid,
  };
};

export const validatePrenatalCheckForm = (
  formState: PrenatalCheckFormState
): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};

  if (!formState.checkType?.coding?.[0]?.code) {
    errors.checkType = 'Παρακαλώ επιλέξτε είδος ελέγχου';
  }

  if (!isValidDate(formState.date)) {
    errors.date = 'Η ημερομηνία δεν είναι έγκυρη ή είναι μελλοντική';
  }

  const fileValidation = validateFileForUpload(formState.file);
  if (formState.file && !fileValidation.isPdf) {
    errors.file = 'Το αρχείο πρέπει να είναι PDF';
  }
  if (formState.file && !fileValidation.isSizeValid) {
    errors.file = 'Το αρχείο υπερβαίνει το επιτρεπτό μέγεθος (10MB)';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const isFormValid = (formState: PrenatalCheckFormState): boolean => {
  return (
    !!formState.checkType?.coding?.[0]?.code && isValidDate(formState.date)
  );
};

// -----------------------------------------------------------------------------
// File Upload
// -----------------------------------------------------------------------------

export const handleFileUpload = async (
  file: File,
  medplum: MedplumClient
): Promise<{ title: string; url: string; contentType: string }> => {
  const binary = await medplum.createBinary({
    data: file,
    filename: file.name,
    contentType: PDF_MIME_TYPE,
  });

  return {
    title: file.name,
    url: getReferenceString(binary),
    contentType: PDF_MIME_TYPE,
  };
};

// -----------------------------------------------------------------------------
// Build DiagnosticReport Payload
// -----------------------------------------------------------------------------

export const buildPrenatalCheckPayload = ({
  formState,
  patient,
  encounterId,
  attachment,
}: {
  formState: PrenatalCheckFormState;
  patient: Patient;
  encounterId?: string | null;
  attachment?: Attachment;
}): DiagnosticReport => {
  const extensions: DiagnosticReport['extension'] = [];

  if (formState.reasonForTest) {
    extensions.push({
      url: PRENATAL_CHECK_REASON_EXTENSION_URL,
      valueString: formState.reasonForTest,
    });
  }

  if (formState.testType) {
    extensions.push({
      url: PRENATAL_CHECK_TEST_TYPE_EXTENSION_URL,
      valueString: formState.testType,
    });
  }

  return {
    resourceType: 'DiagnosticReport',
    status: 'final',
    category: [
      {
        coding: [
          {
            system: ARKON.CS.REPORTS.CATEGORY.BASE,
            code: MEDICAL_HISTORY_REPORT_CATEGORIES.PRENATAL_CHECK,
            display: 'Prenatal Check',
          },
        ],
        text: 'Προγεννητικός έλεγχος',
      },
    ],
    code: formState.checkType || {
      text: 'Προγεννητικός έλεγχος',
    },
    subject: createReference(patient),
    encounter: encounterId
      ? { reference: `Encounter/${encounterId}` }
      : undefined,
    effectiveDateTime: formState.date
      ? new Date(formState.date + 'T00:00:00Z').toISOString()
      : undefined,
    conclusion: formState.observations || undefined,
    conclusionCode: buildConclusionCode(formState.result),
    extension: extensions.length > 0 ? extensions : undefined,
    presentedForm: attachment ? [attachment] : undefined,
  };
};

// -----------------------------------------------------------------------------
// Save/Update/Delete Operations
// -----------------------------------------------------------------------------

const resolveAttachment = async (
  medplum: MedplumClient,
  formState: PrenatalCheckFormState,
  existingReport?: DiagnosticReport | null
): Promise<Attachment | undefined> => {
  if (formState.file) {
    const uploadResult = await handleFileUpload(formState.file, medplum);
    return {
      title: uploadResult.title,
      url: uploadResult.url,
      contentType: uploadResult.contentType,
    };
  }
  return existingReport?.presentedForm?.[0];
};

export const savePrenatalCheck = async ({
  medplum,
  patient,
  encounterId,
  formState,
  existingReport,
}: PrenatalCheckSaveParams): Promise<DiagnosticReport> => {
  const attachment = await resolveAttachment(
    medplum,
    formState,
    existingReport
  );
  const payload = buildPrenatalCheckPayload({
    formState,
    patient,
    encounterId,
    attachment,
  });

  if (existingReport?.id) {
    return medplum.updateResource({
      ...existingReport,
      ...payload,
      id: existingReport.id,
      resourceType: 'DiagnosticReport',
    });
  }

  return medplum.createResource(payload);
};

export const deletePrenatalCheck = async (
  medplum: MedplumClient,
  report: DiagnosticReport
): Promise<void> => {
  if (!report.id) return;
  await medplum.deleteResource('DiagnosticReport', report.id);
};

// -----------------------------------------------------------------------------
// Fetch Prenatal Checks
// -----------------------------------------------------------------------------

export const fetchPrenatalChecks = async (
  medplum: MedplumClient,
  patient: Patient
): Promise<DiagnosticReport[]> => {
  if (!patient?.id) return [];

  const categoryCode = `${ARKON.CS.REPORTS.CATEGORY.BASE}|${MEDICAL_HISTORY_REPORT_CATEGORIES.PRENATAL_CHECK}`;

  return medplum.searchResources('DiagnosticReport', {
    subject: `Patient/${patient.id}`,
    category: categoryCode,
    _sort: '-date',
  });
};
